import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    model,
    OnInit,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { debounceTime, fromEvent, Subscription } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { LoaderModalComponent } from '@connect-services/client/components/modals/loader-modal/loader-modal.component';
import {
    BayDrawer,
    CableAction,
    FiberingSideEnum,
    LinkToDelete,
    RestoreActionEnum,
    SelectedNode,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import {
    CassetteEditComponent,
    UpdatePositionOutputValue,
} from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/cassette-edit/cassette-edit.component';
import { SynopticErrorsComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/errors/synoptic-errors.component';
import { SideCablesEditComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/side-cables-edit/side-cables-edit.component';
import { SynopticPlanEditHeaderComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/synoptic-plan-edit-header/synoptic-plan-edit-header.component';
import {
    CableByUsage,
    CableDirectionEnum,
    FiberingResult,
    PlanTypeEnum,
    SynopticBoxPlanCable,
    SynopticBoxPlanCableBySide,
    SynopticPositionListValues,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { EntityEdit } from '@connect-services/client/services/edit-mode-state.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { BoxPlanEditModeStateService } from '@connect-services/client/services/synoptic/edit/box-plan-edit-mode-state.service';
import {
    CableFiberAnchor,
    CableTubeAnchor,
    CassetteAnchor,
    CassettePositionAnchor,
    SynopticPlanEditService,
} from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit.service';
import { SynopticPlanEditApiCallerService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-api-caller.service';
import { SynopticPlanEditDrawerService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-drawer.service';
import { SynopticPlanEditModalService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-modal.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticPlanBuilderService } from '@connect-services/client/services/synoptic/synoptic-plan-builder.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { SelectComponent } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CassetteEditComponent,
        LoadingComponent,
        RessourcesModule,
        SelectComponent,
        SideCablesEditComponent,
        SynopticErrorsComponent,
        SynopticPlanEditHeaderComponent,
    ],
    providers: [SynopticPlanEditService],
    selector: 'app-synoptic-plan-edit',
    styleUrl: './synoptic-plan-edit.component.scss',
    templateUrl: './synoptic-plan-edit.component.html',
})
export class SynopticPlanEditComponent implements AfterViewInit, OnInit {
    public readonly cassettes = model<SynopticPlanItem[][]>([]);
    public readonly depositId = input.required<string>();
    public readonly drawer = input<BayDrawer | undefined>();
    public readonly errors = model<string[]>([]);
    public readonly layerId = input.required<string>();
    public readonly leaveEdition = output<void>();
    public readonly selectedNode = input.required<SelectedNode>();
    public readonly sideCables = model<SynopticBoxPlanCableBySide>({ enteringCables: [], outgoingCables: [] });
    public readonly type = input.required<PlanTypeEnum>();

    // public properties needed to be accessible by the parent synoptic-bay-plan-edit
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly hasSelectedCassette = computed<boolean>(() => {
        return this.synopticPlanEditService.selectedCassettes().size > 0;
    });
    public readonly hasSelection = computed<boolean>(() => {
        return (
            !!this.synopticPlanEditService.selectedCables().size ||
            !!this.synopticPlanEditService.selectedCassettes().size
        );
    });
    public readonly isRestoreUndoEdition = signal<boolean>(false);

    protected readonly cassetteOptions = computed<{ label: string; value: string }[]>(() => {
        return this.cassettes().map((cassette: SynopticPlanItem[]) => {
            const cassetteNumber = parseInt(cassette[0].numero_cassette ?? '') || 0;
            const cassetteKey = cassette[0].cassette ?? '';

            return {
                label: `${cassetteNumber} - ${cassetteKey}`,
                value: cassetteKey,
            };
        });
    });
    protected readonly cassettesByKeys = computed<Array<[string, SynopticPlanItem[]]>>(() => {
        return this.cassettes().map((cassette: SynopticPlanItem[]) => {
            const key = cassette.find((cassetteItem: SynopticPlanItem) => cassetteItem.cassette)?.cassette;

            return [key ?? '', cassette];
        }, []);
    });
    protected readonly deletedCableKeys: Set<string> = new Set();
    protected readonly displayedCassettes = computed<SynopticPlanItem[][]>(() => {
        return this.displayedCassettesByKeys().map(([, cassette]: [string, SynopticPlanItem[]]) => cassette);
    });
    protected readonly displayedCassettesByKeys = computed<Array<[string, SynopticPlanItem[]]>>(() => {
        const selectedCassetteKey = this.selectedCassetteKey();
        if (!selectedCassetteKey) {
            return this.cassettesByKeys();
        }

        return this.cassettesByKeys().filter(([cassetteKey]: [string, SynopticPlanItem[]]) => {
            return cassetteKey === selectedCassetteKey;
        });
    });
    protected editSessionId!: string;
    protected initialCables: SynopticBoxPlanCableBySide = { enteringCables: [], outgoingCables: [] };
    protected initialCassettes: SynopticPlanItem[][] = [];
    protected readonly isLoadingData = signal<boolean>(true);
    protected readonly openedCassettes = computed<Array<[string, SynopticPlanItem[]]>>(() => {
        return this.displayedCassettesByKeys().filter((value: [string, SynopticPlanItem[]]) =>
            this.openedCassettesKeys().includes(value[0]),
        );
    });
    protected readonly openedCassettesKeys = signal<string[]>([]);
    protected readonly planTypeText = computed<string>(() => {
        const type = this.translocoService.translate(`synopticPlan.${this.type()}`);

        return this.translocoService.translate('synopticPlan.endOfPlan', {
            type: type.toLowerCase(),
        });
    });
    protected positionListValues: SynopticPositionListValues = { assignments: [], engineeringTypes: [], functions: [] };
    protected readonly selectedCassetteKey = signal<string | null>(null);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly CableDirectionEnum = CableDirectionEnum;
    protected readonly PlanTypeEnum = PlanTypeEnum;
    protected readonly RestoreActionEnum = RestoreActionEnum;

    private readonly boxPlanContainer = viewChild<ElementRef>('boxPlanContainer');
    private readonly isViewInit = signal<boolean>(false);
    private resizeSubscription?: Subscription;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly boxPlanEditModeStateService = inject(BoxPlanEditModeStateService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly selectSynopticService = inject(SelectSynopticService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly synopticPlanBuilderService = inject(SynopticPlanBuilderService);
    private readonly synopticPlanEditApiCallerService = inject(SynopticPlanEditApiCallerService);
    private readonly synopticPlanEditDrawerService = inject(SynopticPlanEditDrawerService);
    private readonly synopticPlanEditModalService = inject(SynopticPlanEditModalService);
    private readonly synopticPlanEditService = inject(SynopticPlanEditService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.synopticPlanEditDrawerService.destroyConnections();
            this.synopticPlanEditService.resetSelections();
            this.resizeSubscription?.unsubscribe();
        });

        effect(() => {
            const container = this.boxPlanContainer()?.nativeElement;
            const cables = this.sideCables();
            const cassettes = this.displayedCassettesByKeys();
            const openedCassettes = this.openedCassettes();
            const openedKeys = this.openedCassettesKeys();
            if (!this.isViewInit() || !container) {
                return;
            }

            this.redrawCablesOnToggleCassette(cables, cassettes, openedCassettes, openedKeys);
        });

        effect(() => {
            const selectedCassetteAnchor = this.synopticPlanEditService.selectedCassetteAnchor();
            const selectedTubeAnchor = this.synopticPlanEditService.selectedTubeAnchor();
            const cables = this.sideCables();
            const drawer = untracked(() => this.drawer());

            if (!selectedCassetteAnchor || !selectedTubeAnchor) {
                return;
            }

            this.addTubeFibersToCassette(cables, selectedCassetteAnchor, selectedTubeAnchor, drawer);
            this.synopticPlanEditService.resetSelections();
        });

        effect(() => {
            const selectedCassettePosition = this.synopticPlanEditService.selectedCassettePosition();
            const selectedCableFiber = this.synopticPlanEditService.selectedCableFiber();
            const cables = this.sideCables();
            const drawer = untracked(() => this.drawer());

            if (!selectedCassettePosition || !selectedCableFiber) {
                return;
            }

            this.addFiberToCassettePosition(cables, selectedCassettePosition, selectedCableFiber, drawer);
            this.synopticPlanEditService.resetSelections();
        });

        effect(() => {
            const cassettes = this.cassettes();
            const cables = this.sideCables();
            const projectId = this.projectBloc.value()?.id;
            const layerId = untracked(() => this.layerId());
            const node = untracked(() => this.selectedNode().dataNode);
            const isRestoreUndoEdition = untracked(() => this.isRestoreUndoEdition());
            const isViewInit = untracked(() => this.isViewInit());

            if (!projectId || !isViewInit || this.isLoadingData() || isRestoreUndoEdition) {
                return;
            }

            this.boxPlanEditModeStateService.updateEditModeState({
                cables,
                cassettes,
                drawerId: this.drawer()?.id,
                initialCables: this.initialCables,
                initialCassettes: this.initialCassettes,
                layerId,
                node,
                projectId,
            });
        });
    }

    public automaticFibering(fiberingSide: FiberingSideEnum): void {
        const cablesToFiber = this.synopticPlanEditService.getCablesToSelect(this.sideCables(), fiberingSide);
        const cassettesToFiber = this.synopticPlanEditService.getCassettesToSelect(this.cassettesByKeys());

        cassettesToFiber.forEach(([cassetteKey]: [string, SynopticPlanItem[]]) => {
            Object.values(cablesToFiber).forEach((cables: SynopticBoxPlanCable[], index: number) => {
                this.addCablesFibersToCassette(cables, cassetteKey, index);
            });
        });
        this.synopticPlanEditService.resetSelections();
    }

    public ngAfterViewInit(): void {
        this.resizeSubscription = fromEvent(window, 'resize')
            .pipe(debounceTime(150))
            .subscribe(() => {
                this.redrawCablesOnToggleCassette(
                    this.sideCables(),
                    this.displayedCassettesByKeys(),
                    this.openedCassettes(),
                    this.openedCassettesKeys(),
                );
            });
        this.isViewInit.set(true);
    }

    public async ngOnInit(): Promise<void> {
        this.editSessionId = this.projectEditModeService.getEditSession()?.id ?? '';
        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        this.synopticPlanEditDrawerService.deleteLink.subscribe((linkToDelete: LinkToDelete) => {
            this.deleteLink(linkToDelete);
        });

        const editProjectState = this.boxPlanEditModeStateService.getState(projectId);
        const currentState = editProjectState[this.layerId()]?.[this.selectedNode().dataNode.uuid];

        const [cassettes, cables, positionListValues] = await Promise.all([
            this.getInitialCassettes(currentState),
            this.getInitialCables(currentState),
            this.synopticPlanEditApiCallerService.getPositionListValues(this.depositId()),
        ]);
        this.initialCassettes = cassettes;
        this.initialCables = cables;
        this.positionListValues = positionListValues;
        this.cassettes.set(cassettes);
        this.sideCables.set(cables);
        this.isLoadingData.set(false);
    }

    public resetFibering(fiberingSide: FiberingSideEnum): void {
        const cablesToReset = this.synopticPlanEditService.getCablesToSelect(this.sideCables(), fiberingSide);
        const cassettesToReset = this.synopticPlanEditService.getCassettesToSelect(this.cassettesByKeys());

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return cassettes.map((cassette: SynopticPlanItem[]) => {
                return cassette.map((cassetteItem: SynopticPlanItem) =>
                    this.synopticPlanEditService.getResetCassetteItem(
                        cassetteItem,
                        fiberingSide,
                        cassettesToReset,
                        cablesToReset,
                    ),
                );
            });
        });
        this.synopticPlanEditService.resetSelections();
    }

    public updateCassettePosition(outputValue: UpdatePositionOutputValue): void {
        this.cassettes.update((currentValue: SynopticPlanItem[][]) => {
            return currentValue.map((currentCassette: SynopticPlanItem[]) => {
                return this.synopticPlanEditService.updateCassettePosition(
                    currentCassette,
                    outputValue.positionValue,
                    outputValue.fiber,
                );
            });
        });
    }

    public async updateCassettesPositions(): Promise<void> {
        const positionValue = await this.synopticPlanEditModalService.openPositionEditModal(this.positionListValues);
        if (!positionValue) {
            return;
        }

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return cassettes.map((cassetteItems: SynopticPlanItem[]) => {
                const isSelectedCassette = cassetteItems.some((cassetteItem: SynopticPlanItem) =>
                    this.synopticPlanEditService.selectedCassettes().has(cassetteItem.cassette ?? ''),
                );
                if (!isSelectedCassette) {
                    return cassetteItems;
                }

                return this.synopticPlanEditService.updateCassettePosition(cassetteItems, positionValue);
            });
        });
        this.synopticPlanEditService.resetSelections();
    }

    protected async addCables(side: CableDirectionEnum): Promise<void> {
        const selectedCables = await this.synopticPlanEditModalService.openCreateCableModal(
            this.sideCables(),
            this.deletedCableKeys,
            { boxBay: this.selectedNode().dataNode.uuid, deposit: this.depositId(), editSession: this.editSessionId },
            side,
        );
        if (!selectedCables) {
            return;
        }

        const cablesToAdd = await Promise.all(
            selectedCables.map((selectedCable: CableByUsage) => {
                return this.synopticPlanEditApiCallerService.getCableData(
                    this.depositId(),
                    selectedCable.businessIdentifier,
                    side,
                );
            }),
        );

        const availableCables = cablesToAdd.filter((cable): cable is SynopticBoxPlanCable => !!cable);

        this.addCablesToCassette(side, availableCables);
        availableCables.forEach((cable: SynopticBoxPlanCable) => {
            this.deletedCableKeys.delete(cable.label ?? '');
        });
    }

    protected async addCassette(): Promise<void> {
        const cassetteReference = await this.synopticPlanEditModalService.openCreateCassetteModal(this.depositId());
        if (!cassetteReference) {
            return;
        }

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return this.synopticPlanEditService.addCassette(cassettes, cassetteReference, this.drawer());
        });
    }

    protected async checkFibering(): Promise<void> {
        const errors = await this.synopticPlanEditApiCallerService.checkFibering(
            this.selectedNode().dataNode.uuid,
            this.depositId(),
            this.cassettes().flat(),
            this.type(),
            this.selectSynopticService.selectedSynoptic()?.znUuid ?? '',
        );
        this.errors.set(errors);
    }

    protected closeCassette(cassetteKey: string): void {
        this.openedCassettesKeys.update((currentValue: string[]) => {
            return currentValue.filter((value: string) => value !== cassetteKey);
        });
    }

    protected async onLeaveEdition(): Promise<void> {
        const projectId = this.projectBloc.value()?.id ?? '';

        const shouldLeaveEdition = await this.synopticPlanEditService.leaveEdition(
            projectId,
            this.layerId(),
            this.selectedNode().dataNode.uuid,
        );

        if (!shouldLeaveEdition) {
            return;
        }

        this.leaveEdition.emit();
    }

    protected openCassette(cassetteKey: string): void {
        this.openedCassettesKeys.update((currentValue: string[]) => [...currentValue, cassetteKey]);
    }

    protected redrawTube(): void {
        this.redrawCablesOnToggleCassette(
            this.sideCables(),
            this.cassettesByKeys(),
            this.openedCassettes(),
            this.openedCassettesKeys(),
        );
    }

    protected removeCable(cableToRemove: SynopticBoxPlanCable, side: CableDirectionEnum): void {
        this.sideCables.update((sideCables: SynopticBoxPlanCableBySide) => {
            return this.synopticPlanEditService.removeCableFromSide(sideCables, cableToRemove.code, side);
        });
        this.deletedCableKeys.add(cableToRemove.label ?? '');

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return this.synopticPlanEditService.removeCableLinksFromCassettes(
                cassettes,
                this.cassettesByKeys(),
                cableToRemove.code,
                this.sideCables(),
                side,
            );
        });
    }

    protected removeCassette(cassetteKeyToRemove: string): void {
        this.synopticPlanEditDrawerService.reset();

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return cassettes.filter((cassette: SynopticPlanItem[]) => {
                return !cassette.some(
                    (cassetteItem: SynopticPlanItem) => cassetteItem.cassette === cassetteKeyToRemove,
                );
            });
        });

        this.closeCassette(cassetteKeyToRemove);
        this.synopticPlanEditService.resetSelections();
        this.changeDetectorRef.detectChanges();
    }

    protected restoreUndoEdition(action: RestoreActionEnum): void {
        const projectId = this.projectBloc.value()?.id;
        const eventItem = this.boxPlanEditModeStateService.getEventCanceledOrRestored(action, projectId);
        if (!eventItem) {
            return;
        }

        this.isRestoreUndoEdition.set(true);
        this.cassettes.set(eventItem['cassettes']);
        this.sideCables.set(eventItem['cables']);

        // Needed to avoid to have isRestoreUndoEdition = false in the effect which check sideCables & cassettes updates
        requestAnimationFrame(() => {
            this.isRestoreUndoEdition.set(false);
        });
    }

    protected updateCableOrder(cableAction: CableAction, side: CableDirectionEnum): void {
        this.sideCables.update((cables: SynopticBoxPlanCableBySide) => {
            return this.synopticPlanEditService.moveCableUpAndDown(cables, cableAction, side);
        });
    }

    protected async validateFibering(): Promise<void> {
        const dialogRef = this.matDialog.open(LoaderModalComponent, {
            autoFocus: false,
            data: {
                title: this.translocoService.translate('synopticPlan.reviewAndSave'),
            },
            minWidth: '40vw',
        });

        if (dialogRef.componentInstance) {
            dialogRef.componentInstance.setMessage(
                `${this.translocoService.translate('synopticPlan.validatingUpdates')}...`,
            );
        }

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            dialogRef.close();

            return;
        }

        const validationResult: FiberingResult = await this.synopticPlanEditApiCallerService.validateFibering(
            this.selectedNode().dataNode.uuid,
            this.depositId(),
            this.cassettes().flat(),
            this.type(),
            this.selectSynopticService.selectedSynoptic()?.znUuid ?? '',
        );
        this.errors.set(validationResult.errors);

        if (!validationResult.extraObjects?.length || !validationResult.success) {
            dialogRef.componentInstance.setHasError(true);
            dialogRef.componentInstance.setMessage(this.translocoService.translate('synopticPlan.checkFiberingError'));
            setTimeout(() => {
                dialogRef.close();
            }, 2000);

            return;
        }

        if (dialogRef.componentInstance) {
            dialogRef.componentInstance.setMessage(
                `${this.translocoService.translate('synopticPlan.savingUpdates')}...`,
            );
        }
        void this.boxPlanEditModeStateService.saveFiberingUpdatesIntoProjectState(
            projectId,
            this.depositId(),
            validationResult.extraObjects,
        );

        dialogRef.close();
    }

    private addCablesFibersToCassette(cables: SynopticBoxPlanCable[], cassetteKey: string, index: number): void {
        const side = !index ? CableDirectionEnum.LEFT : CableDirectionEnum.RIGHT;
        cables.forEach((cable: SynopticBoxPlanCable) => {
            Object.keys(cable.tubes).forEach((tubeIndex: string) => {
                this.addTubeFibersToCassette(
                    this.sideCables(),
                    { key: cassetteKey, side },
                    { cableCode: cable.code, side, tubeIndex: Number(tubeIndex) },
                    this.drawer(),
                );
            });
        });
    }

    private addCablesToCassette(side: CableDirectionEnum, cables: SynopticBoxPlanCable[]): void {
        this.sideCables.update((currentCables: SynopticBoxPlanCableBySide) => {
            return {
                enteringCables: [...currentCables.enteringCables, ...(side === CableDirectionEnum.LEFT ? cables : [])],
                outgoingCables: [...currentCables.outgoingCables, ...(side === CableDirectionEnum.RIGHT ? cables : [])],
            };
        });
    }

    private addFiberToCassettePosition(
        cables: SynopticBoxPlanCableBySide,
        cassetteAnchor: CassettePositionAnchor,
        cableFiberAnchor: CableFiberAnchor,
        drawer?: BayDrawer,
    ): void {
        const cable = Object.values(cables)
            .flat()
            .find((sideCable: SynopticBoxPlanCable) => sideCable.code === cableFiberAnchor.cableCode);

        if (!cable) {
            return;
        }

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return this.synopticPlanEditService.addLinkFiberCassettePosition(
                cassettes,
                cable,
                cassetteAnchor,
                cableFiberAnchor,
                drawer,
            );
        });
    }

    private addTubeFibersToCassette(
        cables: SynopticBoxPlanCableBySide,
        cassetteAnchor: CassetteAnchor,
        cableTubeAnchor: CableTubeAnchor,
        drawer?: BayDrawer,
    ): void {
        const cable = Object.values(cables)
            .flat()
            .find((sideCable: SynopticBoxPlanCable) => sideCable.code === cableTubeAnchor.cableCode);
        if (!cable) {
            return;
        }

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return this.synopticPlanEditService.addLinkTubeCassette(
                cassettes,
                cable,
                cassetteAnchor,
                cableTubeAnchor,
                drawer,
            );
        });
    }

    private deleteLink(linkToDelete: LinkToDelete): void {
        const resetType =
            linkToDelete.side === CableDirectionEnum.LEFT ? FiberingSideEnum.ENTERING : FiberingSideEnum.OUTGOING;

        this.cassettes.update((cassettes: SynopticPlanItem[][]) => {
            return cassettes.map((cassette: SynopticPlanItem[]) => {
                return cassette.map((cassetteItem: SynopticPlanItem) => {
                    if (!this.synopticPlanEditService.isMatchingLink(cassetteItem, linkToDelete)) {
                        return cassetteItem;
                    }
                    const cablesToReset = this.synopticPlanEditService.getCablesToSelect(this.sideCables(), resetType);
                    const cassettesToReset = this.synopticPlanEditService.getCassettesToSelect(this.cassettesByKeys());

                    return this.synopticPlanEditService.getResetCassetteItem(
                        cassetteItem,
                        resetType,
                        cassettesToReset,
                        cablesToReset,
                    );
                });
            });
        });
    }

    private drawCables(sideCables: SynopticBoxPlanCableBySide, cassettes: Array<[string, SynopticPlanItem[]]>): void {
        const containerRef = document.getElementById(`boxPlan_view`);
        try {
            AssertService.isHtmlElement(containerRef);
        } catch {
            return;
        }

        cassettes.forEach(([cassetteKey, cassette]: [string, SynopticPlanItem[]]) => {
            [CableDirectionEnum.LEFT, CableDirectionEnum.RIGHT].forEach((side: CableDirectionEnum) => {
                const cassetteRef = containerRef.querySelector(
                    `#plan_cassette_${cassetteKey} #plus_${cassetteKey}_${side}`,
                );

                try {
                    AssertService.isHtmlElement(cassetteRef);

                    this.synopticPlanEditDrawerService.drawTubeCassetteFibers(
                        sideCables,
                        side,
                        cassetteRef,
                        containerRef,
                        cassette,
                        cassetteKey,
                    );
                } catch (error: unknown) {
                    Sentry.captureException(error);
                    this.snackBarService.openErrorSnackBar('errorDefault');
                }
            });
        });
    }

    private getInitialCables(currentState: EntityEdit): Promise<SynopticBoxPlanCableBySide> {
        if (Object.values(this.sideCables()).flat().length || !!this.drawer()) {
            return Promise.resolve(this.sideCables());
        }

        if (currentState?.properties?.['cables'] && Object.keys(currentState.properties['cables']).length) {
            return Promise.resolve(currentState.properties['cables']);
        }

        return this.synopticPlanEditApiCallerService.getSideCables(this.selectedNode(), this.depositId());
    }

    private async getInitialCassettes(currentState: EntityEdit): Promise<SynopticPlanItem[][]> {
        if (this.cassettes().length) {
            return this.cassettes();
        }

        if (Array.isArray(currentState?.properties?.['cassettes'])) {
            return currentState.properties['cassettes'];
        }

        const boxPlan = await this.synopticPlanEditApiCallerService.getBoxPlan(
            this.selectedNode().dataNode.uuid,
            this.depositId(),
            this.selectSynopticService.selectedSynoptic()?.znUuid ?? '',
        );

        return this.synopticPlanBuilderService.sortCassettes(boxPlan);
    }

    private redrawCablesOnToggleCassette(
        sideCables: SynopticBoxPlanCableBySide,
        cassettes: Array<[string, SynopticPlanItem[]]>,
        openedCassettes: Array<[string, SynopticPlanItem[]]>,
        openedKeys: string[],
    ): void {
        const closedCassettes = cassettes.filter(
            (value: [string, SynopticPlanItem[]]) => !openedKeys.includes(value[0]),
        );

        const containerRef = document.getElementById(`boxPlan_view`);
        requestAnimationFrame(() => {
            this.synopticPlanEditDrawerService.reset();
            try {
                AssertService.isHtmlElement(containerRef);

                this.drawCables(sideCables, closedCassettes);

                openedCassettes.forEach(([key, items]: [string, SynopticPlanItem[]]) => {
                    this.synopticPlanEditDrawerService.drawCassetteFibers(items, key, containerRef);
                });
            } catch (error: unknown) {
                Sentry.captureException(error);
                this.snackBarService.openErrorSnackBar('errorDefault');
            }
        });
    }
}
